import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';

import { BreadcrumbItemType, BreadcrumbSeparatorType } from 'antd/es/breadcrumb/Breadcrumb';

import { ItemProvider, useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import CustomRequestProvider from '@app/components/CustomRequestProvider/CustomRequestProvider';
import {
	EmailShare,
	FacebookShare,
	LinkedinShare,
	TwitterShare,
} from '@app/components/LocalCommon/ShareButtons/ShareButtons';
import HelmetProvider from '@app/components/UI/HelmetProvider/HelmetProvider';
import Masthead from '@app/components/UI/MastHead/Masthead';
import { ContentEntity, Topic } from '@app/objects/ContentTopic';
import '@app/scss/components/blog.scss';

const ArticleContent: React.FC<{ topic: Topic | undefined }> = ({ topic }) => {
	const location = useLocation();
	const [breadcrumbs, setBreadcrumbs] = React.useState<Array<Partial<BreadcrumbItemType & BreadcrumbSeparatorType>> | undefined>();

	const context = useItemProviderContext<ContentEntity>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { item },
	} = context;

	const imageAlts = {
		'10-common-cancer-signs-in-pets': 'Cancer in pets',
		'5-ways-to-care-for-a-pet-with-urinary-incontinence': 'Pet urinary incontinence ',
		'chronic-kidney-disease-in-cats': 'Cat kidney disease',
		'does-my-senior-dog-have-canine-cognitive-dysfunction': 'Cognitive dysfunction dog',
		'5-easy-ways-to-adjust-your-senior-cat%E2%80%99s-environmental-enrichment': 'Senior cat',
		'7-ways-to-memorialize-your-cherished-pet': 'Pet memorial',
		'6-ways-to-care-for-your-paralyzed-pet': 'Paralyzed dog',
		'caring-for-a-special-needs-senior-pet': 'Special needs pet',
		'how-will-i-know-when-it-is-time-to-end-my-pet%E2%80%99s-suffering': 'How do I know pet suffering',
		'4-ways-regular-veterinary-care-keeps-your-senior-pet-healthy': 'Senior pet veterinary care',
		'pet-euthanasia-your-questions-answered': 'Pet euthanasia',
		'quality-of-life-assessment-a-critical-tool-for-your-senior-pet': 'Quality of life assessment',
		'5-ways-to-keep-your-senior-pet-active': 'Keep senior pet active',
	};

	React.useEffect(() => {
		if (item) {
			const bc: Array<Partial<BreadcrumbItemType & BreadcrumbSeparatorType>> | undefined = [
				{
					path: '/blog',
					title: 'Blog',
				},
				{
					path: `/blog/${topic?.url || item?.topic?.url || ''}`,
					title: topic?.name || item?.topic?.name || '',
				},
				{
					path: '',
					title: item?.title,
				},
			];

			setBreadcrumbs(bc);
		}
	}, [item]);

	return (
		item ? (
			<>
				{breadcrumbs && (
					<>
						{item.title && (
							<Helmet>
								<title>{`${item.title} | Lap of Love`}</title>
								<meta property="og:title" content={`${item.title} | Lap of Love`} />
								<meta name="twitter:title" content={`${item.title} | Lap of Love`} />
								<meta name="description" content={item.description || undefined} />
								<meta property="og:description" content={item.description || undefined} />
								<meta name="twitter:description" content={item.description || undefined} />
							</Helmet>
						)}
						<Masthead
							title={item.title}
							imageSrc={item.avatar}
							breadcrumbs={breadcrumbs}
							backPath={{
								path: '/blog',
								name: 'Back to blog',
							}}
							imageAlt={imageAlts[item.url]}
						/>
					</>
				)}
				<div className="share-icons blog__share-icons">
					<p>Share This Post:</p>
					<FacebookShare url={location.pathname} />
					<LinkedinShare url={location.pathname} />
					<TwitterShare url={location.pathname} />
					<EmailShare url={location.pathname} />
				</div>
				<div className="article-page" dangerouslySetInnerHTML={{ __html: item.fullText }} />
			</>
		) : <BounceLoader />
	);
};

const Article: React.FC = () => {
	const params = useParams<{ topic: string; id: string }>();

	return (
		<HelmetProvider>
			{(page) => (
				<CustomRequestProvider version="v2">
					<ItemProvider<ContentEntity>
						type={`contentTopics/blog/${params.topic}/${params.id}/entity`}
						loadRequest={`contentTopics/blog/${params.topic}/${params.id}/entity`}
						id={0}
						skipInitLoad={Boolean(page?.post)}
						item={page?.post}
					>
						<ArticleContent topic={page?.topic} />
					</ItemProvider>
				</CustomRequestProvider>
			)}
		</HelmetProvider>
	);
};

export default Article;
